import { useMemo, useState, useSyncExternalStore } from "react";
import type { TaskInput } from "@/types";
import { Button } from "@/ui";
import { battlePlanItemStore, battlePlanStore, taskStore } from "@/services/client";
import { EmptyState } from "@/features/shell/EmptyState";
import { type Period, periodKeyFor, periodLabel, shiftPeriod } from "./periodKey";

const PREV = "‹";
const NEXT = "›";

const STATUS_LABEL: Record<TaskInput["status"], string> = {
  planned: "Planned",
  inProgress: "In progress",
  completed: "Done",
  failed: "Failed",
  delayed: "Delayed",
};

/**
 * A battle plan: the period-boxed execution list for one day or one week. It is not
 * a plan of its own — it is a *selection* of existing tasks, so an item that points
 * at a task no longer on the feed simply does not render.
 */
export function BattlePlanPanel({ kind, title }: { kind: Period; title: string }) {
  const plans = useSyncExternalStore(battlePlanStore.subscribe, battlePlanStore.getSnapshot);
  const items = useSyncExternalStore(
    battlePlanItemStore.subscribe,
    battlePlanItemStore.getSnapshot,
  );
  const tasks = useSyncExternalStore(taskStore.subscribe, taskStore.getSnapshot);
  const [anchor, setAnchor] = useState(() => new Date());

  const periodKey = periodKeyFor(kind, anchor);

  const rows = useMemo(() => {
    const plan = plans.find(
      (candidate) => candidate.fields.kind === kind && candidate.fields.periodKey === periodKey,
    );
    if (plan === undefined) return null;

    const taskById = new Map(tasks.map((task) => [task.id, task]));
    return items
      .filter((item) => item.fields.battlePlanId === plan.id)
      .sort((a, b) => (a.fields.sortKey < b.fields.sortKey ? -1 : 1))
      .map((item) => ({ item, task: taskById.get(item.fields.taskId) }))
      .filter((row) => row.task !== undefined);
  }, [plans, items, tasks, kind, periodKey]);

  return (
    <section className="bp">
      <header className="bp-head">
        <h3 className="bp-title">{title}</h3>
        <div className="bp-nav">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label="Previous period"
            onClick={() => setAnchor(shiftPeriod(kind, anchor, -1))}
          >
            {PREV}
          </Button>
          <span className="bp-period">{periodLabel(kind, anchor)}</span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label="Next period"
            onClick={() => setAnchor(shiftPeriod(kind, anchor, 1))}
          >
            {NEXT}
          </Button>
        </div>
      </header>

      {rows === null ? (
        <EmptyState
          title={`No ${kind} battle plan for ${periodKey}`}
          hint="A battle plan boxes existing tasks into a period. Create one for this period to fill it."
        />
      ) : rows.length === 0 ? (
        <p className="card-hint">This battle plan is empty.</p>
      ) : (
        <ul className="row-list">
          {rows.map(({ item, task }) => (
            <li key={item.id} className="row">
              <span className="bp-status" data-status={task?.fields.status}>
                {task ? STATUS_LABEL[task.fields.status] : ""}
              </span>
              <span className="row-title">{task?.fields.title}</span>
              {/* An item without a slot falls back to the activity's schedule window —
               * the contract's rule, so the absence is stated rather than blank. */}
              <span className="row-meta">{slotLabel(item.fields.slotStart, item.fields.slotEnd)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const NO_SLOT = "no slot";

function slotLabel(start: string | undefined, end: string | undefined): string {
  if (!start) return NO_SLOT;
  const from = new Date(start).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (!end) return from;
  const to = new Date(end).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${from} – ${to}`;
}
